import { AsyncPipe, NgForOf, NgIf, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { RouterLink } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { LoaderModule } from '@x/common/loader';
import { MediaThumbnailComponent } from '@x/common/media-thumbnail/media-thumbnail.component';
import { MoneyModule } from '@x/common/money';
import { OperationModule, OperationObserverService } from '@x/common/operation';
import { AvailabilityComponent } from '@x/ecommerce-shop/app/core/availability/availability/availability.component';
import { IShopOrderItemView } from '@x/ecommerce-shop/app/core/order/services/order-item-view.service';
import { OrderSummaryService } from '@x/ecommerce-shop/app/core/order/services/order-summary.service';
import { ShopErrorModule } from '@x/ecommerce-shop/app/core/shop-error/shop-error.module';
import { IconUiModule } from '@x/ecommerce-shop/app/core/shop-ui/components/icon-ui/icon-ui.module';
import { OrderItemNamePipe } from '@x/ecommerce-shop/app/core/shop-ui/components/order/order-item-name.pipe';
import { QuantityControlModule } from '@x/ecommerce-shop/app/core/shop-ui/components/quantity-control';
import { UrlModule } from '@x/ecommerce-shop/app/core/url/url.module';
import { UtilsModule } from '@x/ecommerce-shop/app/core/utils/utils.module';
import { OrderItemSource } from '@x/schemas/ecommerce';
import { merge } from 'rxjs';

@Component({
  selector: 'x-order-item',
  standalone: true,
  imports: [
    FaIconComponent,
    IconUiModule,
    MatIconButton,
    MoneyModule,
    UtilsModule,
    RouterLink,
    UrlModule,
    OrderItemNamePipe,
    NgIf,
    QuantityControlModule,
    LoaderModule,
    AsyncPipe,
    OperationModule,
    ShopErrorModule,
    NgTemplateOutlet,
    NgForOf,
    MediaThumbnailComponent,
    AvailabilityComponent,
  ],
  templateUrl: './order-item.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'x-order-item' },
})
export class OrderItemComponent {
  protected readonly orderItemSource = OrderItemSource;
  private _item: IShopOrderItemView;
  @Input() set item(item: IShopOrderItemView) {
    this._item = item;

    const { source, availabilityView } = item;

    this.disableQuantityControl = !(
      source === 'ORDER' &&
      (availabilityView?.status === 'AVAILABLE' || availabilityView?.status === 'OUT_OF_STOCK')
    );
  }
  get item() {
    return this._item;
  }

  @Input() currency?: string | null;
  @Input() mode: 'compact' | 'expanded' = 'compact';

  disableQuantityControl = false;

  private removeItemControl$ = this.operation.control(
    (productVariantId: number) => {
      return this.orderService.removeItem$(productVariantId);
    },
    { preventParallelOperation: true },
  );

  private updateQuantity$ = this.operation.control(
    (args: { quantity: number; productVariantId: number }) => {
      const { quantity, productVariantId } = args;

      return this.orderService.updateQuantity$(quantity, productVariantId);
    },
    { preventParallelOperation: true },
  );

  actions$ = merge(this.removeItemControl$, this.updateQuantity$);

  constructor(
    private operation: OperationObserverService,
    private orderService: OrderSummaryService,
  ) {}

  async updated(quantity: number, item: IShopOrderItemView) {
    await this.updateQuantity$.activate({ quantity, productVariantId: item.variantId });
  }

  async removeItem(variantId: number) {
    await this.removeItemControl$.activate(variantId);
  }
}
